import { useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  CollectSummary,
  NotFoundPage,
  NotFoundRow,
  RedirectImportResult,
  RedirectPage,
  RedirectRow,
} from '../lib/types';
import { DownloadButton, FilePicker } from './imports';
import { StorePicker, useStoreChoice } from './payments';

const PAGE = 50;

/**
 * Adres yönlendirmeleri: eski sitenin ve eski alan adlarının adresleri, adı değişen ürün/kategori adresleri (301/302/410).
 * Eşleştiricinin eklediği kurallar "Eşleştirme" kaynağıyla ve benzerlik puanıyla görünür (F3B-27). Mağazanın kendi
 * sayfalarını gölgeleyen kurallar üstte uyarı olarak listelenir.
 */
export function RedirectsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const manage = can('seo.write');
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const [q, setQ] = useState('');
  const [source, setSource] = useState('');
  const [host, setHost] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [edit, setEdit] = useState<RedirectRow | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const list = useQuery({
    queryKey: ['redirects', storeId, q, source, host, offset],
    queryFn: () => {
      const qs = new URLSearchParams({ storeId, q, limit: String(PAGE), offset: String(offset) });
      if (source) qs.set('source', source);
      if (host !== null) qs.set('host', host);
      return api.get<RedirectPage>(`/v1/admin/redirects?${qs}`);
    },
    enabled: !!storeId,
    placeholderData: keepPreviousData,
  });
  const del = useApiMutation((id: string) => api.del(`/v1/admin/redirects/${id}`), {
    invalidate: [['redirects', storeId]],
    success: t('common.deleted'),
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const data = list.data;
  const filtered = !!q || !!source || host !== null;
  return (
    <PageFrame
      helpKey="page.redirects"
      actions={
        manage &&
        storeId && (
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => setImporting(true)}>{t('admin.redirects.import')}</Button>
            <HelpTip k="redirects.import" />
            <Button variant="primary" onClick={() => setEdit('new')}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.redirects.add')}
            </Button>
            <HelpTip k="redirects.add" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          {!!data?.shadowed.length && (
            <ShadowWarning rows={data.shadowed} manage={manage} onDelete={(id) => del.mutateAsync(id)} />
          )}
          <div className="flex flex-wrap items-end gap-3">
            <Field
              label={t('admin.redirects.search')}
              className="max-w-sm flex-1"
              help={<HelpTip k="redirects.search" />}
            >
              {(p) => (
                <Input
                  {...p}
                  type="search"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setOffset(0);
                  }}
                />
              )}
            </Field>
            <Field label={t('admin.redirects.filterSource')} help={<HelpTip k="redirects.filter_source" />}>
              {(p) => (
                <Select
                  {...p}
                  value={source}
                  onChange={(e) => {
                    setSource(e.target.value);
                    setOffset(0);
                  }}
                >
                  <option value="">{t('admin.redirects.anySource')}</option>
                  {(['manual', 'import', 'auto', 'match'] as const).map((s) => (
                    <option key={s} value={s}>
                      {t(`admin.redirects.sources.${s}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('admin.redirects.filterHost')} help={<HelpTip k="redirects.filter_host" />}>
              {(p) => (
                <Select
                  {...p}
                  value={host === null ? '*' : host}
                  onChange={(e) => {
                    setHost(e.target.value === '*' ? null : e.target.value);
                    setOffset(0);
                  }}
                >
                  <option value="*">{t('admin.redirects.anyHost')}</option>
                  <option value="">{t('admin.redirects.allHosts')}</option>
                  {(data?.hosts ?? []).map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          {!data ? (
            <Spinner label={t('common.loading')} />
          ) : !data.items.length ? (
            <EmptyState
              title={filtered ? t('admin.redirects.noMatch') : t('admin.redirects.empty')}
              description={filtered ? undefined : t('admin.redirects.emptyLead')}
            />
          ) : (
            <>
              <ul className="divide-y divide-line rounded-md border border-line bg-surface">
                {data.items.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                    <div className="min-w-0 flex-1 text-sm">
                      <p className="text-xs text-ink-3">
                        <span className="sr-only">{t('admin.redirects.host')}: </span>
                        {r.fromHost || t('admin.redirects.allHosts')}
                      </p>
                      <p className="break-all font-medium">{r.fromPath}</p>
                      <p className="break-all text-ink-2">
                        <span aria-hidden>↳ </span>
                        <span className="sr-only">{t('admin.redirects.to')}: </span>
                        {r.toUrl ?? t('admin.redirects.goneTarget')}
                      </p>
                      <p className="text-ink-3">
                        {r.hits > 0
                          ? t('admin.redirects.hits', {
                              count: r.hits,
                              date: r.lastHitAt ? formatDate(r.lastHitAt, true) : '—',
                            })
                          : t('admin.redirects.noHits')}
                        {r.note ? ` · ${r.note}` : ''}
                      </p>
                    </div>
                    <Badge tone={r.statusCode === 301 ? 'moss' : r.statusCode === 410 ? 'rose' : 'amber'}>
                      {r.statusCode}
                    </Badge>
                    <Badge tone={r.source === 'match' ? 'pine' : 'neutral'}>
                      {t(`admin.redirects.sources.${r.source}`)}
                      {r.source === 'match' && r.matchKind
                        ? ` · ${t(`admin.redirects.matchKinds.${r.matchKind}`, { defaultValue: r.matchKind })}`
                        : ''}
                      {r.source === 'match' && r.matchScore !== null
                        ? ` · ${t('admin.redirects.matchScore', { score: Math.round(r.matchScore / 10) })}`
                        : ''}
                    </Badge>
                    {manage && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEdit(r)}>
                          {t('common.edit')}
                        </Button>
                        <ConfirmButton
                          message={t('admin.redirects.deleteConfirm', { path: r.fromPath })}
                          onConfirm={() => del.mutateAsync(r.id)}
                        >
                          {t('common.delete')}
                        </ConfirmButton>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-3 text-sm text-ink-2">
                <span>
                  {t('admin.redirects.range', {
                    from: data.offset + 1,
                    to: data.offset + data.items.length,
                    total: data.total,
                  })}
                </span>
                <Button
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                >
                  {t('common.previous')}
                </Button>
                <Button
                  size="sm"
                  disabled={data.offset + data.items.length >= data.total}
                  onClick={() => setOffset(offset + PAGE)}
                >
                  {t('common.next')}
                </Button>
              </div>
            </>
          )}
          <Section title={t('admin.redirects.howTitle')} description={t('admin.redirects.howLead')}>
            <ul className="list-disc space-y-1 ps-5 text-sm text-ink-2">
              <li>{t('admin.redirects.how1')}</li>
              <li>{t('admin.redirects.how2')}</li>
              <li>{t('admin.redirects.how3')}</li>
              <li>{t('admin.redirects.how4')}</li>
              <li>
                {t('admin.redirects.how5')}{' '}
                <Link to="/kirik-linkler" className="text-moss underline underline-offset-2">
                  {t('admin.nav.brokenLinks')}
                </Link>
              </li>
            </ul>
          </Section>
        </>
      )}
      {edit && storeId && (
        <RedirectDialog storeId={storeId} row={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />
      )}
      {importing && storeId && <ImportDialog storeId={storeId} onClose={() => setImporting(false)} />}
    </PageFrame>
  );
}

/** Mağazanın kendi sayfalarını gölgeleyen kurallar (ör. eski siteden kalan `/satis-noktalari` → `/`). */
function ShadowWarning({
  rows,
  manage,
  onDelete,
}: {
  rows: RedirectPage['shadowed'];
  manage: boolean;
  onDelete: (id: string) => Promise<unknown>;
}) {
  const t = useT();
  return (
    <section
      role="alert"
      aria-labelledby="shadow-title"
      className="space-y-2 rounded-md border border-amber/40 bg-amber-tint p-4 text-sm"
    >
      <div className="flex items-center gap-2">
        <h2 id="shadow-title" className="font-semibold text-ink">
          {t('admin.redirects.shadowTitle', { count: rows.length })}
        </h2>
        <HelpTip k="redirects.shadowed" />
      </div>
      <p className="text-ink-2">{t('admin.redirects.shadowLead')}</p>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2">
            <span className="break-all font-medium">{r.fromPath}</span>
            <span aria-hidden>→</span>
            <span className="sr-only">{t('admin.redirects.to')}: </span>
            <span className="break-all text-ink-2">{r.toUrl ?? t('admin.redirects.goneTarget')}</span>
            {manage && (
              <ConfirmButton
                size="sm"
                variant="ghost"
                message={t('admin.redirects.deleteConfirm', { path: r.fromPath })}
                onConfirm={() => onDelete(r.id)}
              >
                {t('admin.redirects.shadowDelete')}
              </ConfirmButton>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function RedirectDialog({
  storeId,
  row,
  initial,
  onClose,
}: {
  storeId: string;
  row: RedirectRow | null;
  /** "Kırık linkler" listesinden açıldıysa eski adres (ve alan adı) hazır gelir. */
  initial?: { fromHost: string; fromPath: string; toUrl?: string | null };
  onClose: () => void;
}) {
  const t = useT();
  const [f, setF] = useState({
    fromHost: row?.fromHost ?? initial?.fromHost ?? '',
    fromPath: row?.fromPath ?? initial?.fromPath ?? '',
    toUrl: row?.toUrl ?? initial?.toUrl ?? '',
    statusCode: row?.statusCode ?? 301,
    note: row?.note ?? '',
  });
  const gone = f.statusCode === 410;
  const payload = () => ({ ...f, toUrl: gone ? null : f.toUrl, note: f.note || null });
  const save = useApiMutation(
    () =>
      row
        ? api.patch(`/v1/admin/redirects/${row.id}`, payload(), { ifMatch: row.version })
        : api.post('/v1/admin/redirects', { ...payload(), storeId }),
    {
      invalidate: [
        ['redirects', storeId],
        ['not-found', storeId],
      ],
      success: t('admin.redirects.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const err = save.error && !save.error.errors.length ? save.error.title : undefined;
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={row ? t('admin.redirects.edit') : t('admin.redirects.add')}
      description={t('admin.redirects.dialogLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('admin.redirects.host')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="redirects.host" />}
          error={fieldError(save.error, 'fromHost')}
        >
          {(p) => (
            <Input
              {...p}
              placeholder={t('admin.redirects.allHosts')}
              value={f.fromHost}
              onChange={(e) => setF({ ...f, fromHost: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.redirects.from')}
          required
          help={<HelpTip k="redirects.from" />}
          error={fieldError(save.error, 'fromPath')}
        >
          {(p) => (
            <Input
              {...p}
              placeholder="/sayfa/kvkk-metni"
              value={f.fromPath}
              onChange={(e) => setF({ ...f, fromPath: e.target.value })}
            />
          )}
        </Field>
        {gone ? (
          <p className="rounded-sm bg-surface-2 p-3 text-sm text-ink-2">{t('admin.redirects.goneLead')}</p>
        ) : (
          <Field
            label={t('admin.redirects.to')}
            required
            help={<HelpTip k="redirects.to" />}
            error={fieldError(save.error, 'toUrl') ?? err}
          >
            {(p) => (
              <Input
                {...p}
                placeholder="/yasal/kvkk-aydinlatma-metni"
                value={f.toUrl}
                onChange={(e) => setF({ ...f, toUrl: e.target.value })}
              />
            )}
          </Field>
        )}
        <Field label={t('admin.redirects.code')} help={<HelpTip k="redirects.code" />}>
          {(p) => (
            <Select
              {...p}
              value={String(f.statusCode)}
              onChange={(e) => setF({ ...f, statusCode: Number(e.target.value) })}
            >
              <option value="301">{t('admin.redirects.permanent')}</option>
              <option value="302">{t('admin.redirects.temporary')}</option>
              <option value="410">{t('admin.redirects.gone')}</option>
            </Select>
          )}
        </Field>
        <Field
          label={t('admin.redirects.note')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="redirects.note" />}
        >
          {(p) => <Input {...p} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />}
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * Kırık linkler (F3B-27): vitrinde 404 veren adresler ve eski alan adından gelip karşılığı bulunamadığı için güvenli
 * yedeğe (türün kategorisi, tüm ürünler veya ana sayfa) gönderilen eski adresler; çok görülen önce. Kural eklenince
 * listeden düşer; "Yok say" listeden çıkarır (sayım sürer). Arşivden toplama, eski alan adının Wayback Machine'deki
 * adreslerini önceden eşler.
 */
export function BrokenLinksPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const manage = can('seo.write');
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const [status, setStatus] = useState<'open' | 'ignored'>('open');
  const [host, setHost] = useState<string | null>(null);
  const [foundVia, setFoundVia] = useState('');
  const [edit, setEdit] = useState<{ fromHost: string; fromPath: string; toUrl: string | null } | null>(null);
  const list = useQuery({
    queryKey: ['not-found', storeId, status, host, foundVia],
    queryFn: () => {
      const qs = new URLSearchParams({ storeId, status, limit: '200' });
      if (host !== null) qs.set('host', host);
      if (foundVia) qs.set('foundVia', foundVia);
      return api.get<NotFoundPage>(`/v1/admin/not-found?${qs}`);
    },
    enabled: !!storeId,
    placeholderData: keepPreviousData,
  });
  const setRowStatus = useApiMutation(
    (v: { id: string; status: 'open' | 'ignored' }) =>
      api.put(`/v1/admin/not-found/${v.id}/status`, { status: v.status }),
    { invalidate: [['not-found', storeId]], success: t('common.saved') },
  );
  const gone = useApiMutation(
    (r: NotFoundRow) =>
      api.post('/v1/admin/redirects', {
        storeId,
        fromHost: r.host,
        fromPath: r.path,
        statusCode: 410,
        toUrl: null,
      }),
    {
      invalidate: [
        ['not-found', storeId],
        ['redirects', storeId],
      ],
      success: t('admin.redirects.goneSaved'),
    },
  );
  const collect = useApiMutation(() => api.post<CollectSummary>('/v1/admin/redirects/collect', { storeId }), {
    invalidate: [
      ['not-found', storeId],
      ['redirects', storeId],
    ],
    onSuccess: (s) => {
      toast.success(
        t('admin.brokenLinks.collected', {
          found: s.found,
          matched: s.matched,
          fallback: s.fallback,
          table: s.table,
        }),
      );
      if (s.failed.length) toast.error(t('admin.brokenLinks.collectFailed', { hosts: s.failed.join(', ') }));
    },
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const data = list.data;
  return (
    <PageFrame
      helpKey="page.broken_links"
      actions={
        manage &&
        storeId && (
          <div className="flex flex-wrap items-center gap-2">
            <Button loading={collect.isPending} onClick={() => collect.mutate(undefined)}>
              {t('admin.brokenLinks.collect')}
            </Button>
            <HelpTip k="broken_links.collect" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          <Section
            title={t('admin.redirects.notFoundTitle')}
            description={t('admin.redirects.notFoundLead')}
            action={<HelpTip k="redirects.not_found" />}
          >
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <Field
                label={t('admin.redirects.notFoundFilter')}
                help={<HelpTip k="redirects.not_found_filter" />}
              >
                {(p) => (
                  <Select
                    {...p}
                    value={status}
                    onChange={(e) => setStatus(e.target.value as 'open' | 'ignored')}
                  >
                    <option value="open">{t('admin.redirects.notFoundOpen')}</option>
                    <option value="ignored">{t('admin.redirects.notFoundIgnored')}</option>
                  </Select>
                )}
              </Field>
              <Field label={t('admin.redirects.filterHost')} help={<HelpTip k="broken_links.host" />}>
                {(p) => (
                  <Select
                    {...p}
                    value={host === null ? '*' : host}
                    onChange={(e) => setHost(e.target.value === '*' ? null : e.target.value)}
                  >
                    <option value="*">{t('admin.redirects.anyHost')}</option>
                    <option value="">{t('admin.brokenLinks.storeHost')}</option>
                    {(data?.hosts ?? []).map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('admin.brokenLinks.foundVia')} help={<HelpTip k="broken_links.found_via" />}>
                {(p) => (
                  <Select {...p} value={foundVia} onChange={(e) => setFoundVia(e.target.value)}>
                    <option value="">{t('admin.redirects.anySource')}</option>
                    {(['request', 'archive', 'search_console'] as const).map((v) => (
                      <option key={v} value={v}>
                        {t(`admin.brokenLinks.via.${v}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
            {!data ? (
              <Spinner label={t('common.loading')} />
            ) : !data.items.length ? (
              <EmptyState
                title={
                  status === 'open'
                    ? t('admin.redirects.notFoundEmpty')
                    : t('admin.redirects.notFoundIgnoredEmpty')
                }
                description={status === 'open' ? t('admin.brokenLinks.emptyLead') : undefined}
              />
            ) : (
              <>
                <ul className="divide-y divide-line rounded-md border border-line bg-surface">
                  {data.items.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                      <div className="min-w-0 flex-1 text-sm">
                        <p className="text-xs text-ink-3">
                          <span className="sr-only">{t('admin.redirects.host')}: </span>
                          {r.host || t('admin.brokenLinks.storeHost')}
                        </p>
                        <p className="break-all font-medium">{r.path}</p>
                        <p className="break-all text-ink-2">
                          {r.redirectedTo
                            ? t('admin.brokenLinks.fallback', { to: r.redirectedTo })
                            : t('admin.brokenLinks.shown404')}
                        </p>
                        <p className="text-ink-3">
                          {r.hits > 0
                            ? t('admin.redirects.notFoundHits', {
                                count: r.hits,
                                date: formatDate(r.lastSeenAt, true),
                              })
                            : t('admin.brokenLinks.notVisited', { date: formatDate(r.firstSeenAt, true) })}
                        </p>
                      </div>
                      {r.kind && (
                        <Badge tone="neutral">
                          {t(`admin.brokenLinks.kinds.${r.kind}`, { defaultValue: r.kind })}
                        </Badge>
                      )}
                      <Badge tone={r.foundVia === 'request' ? 'amber' : 'slate'}>
                        {t(`admin.brokenLinks.via.${r.foundVia}`)}
                      </Badge>
                      {manage && (
                        <span className="flex flex-wrap items-center gap-1">
                          <Button
                            size="sm"
                            onClick={() =>
                              setEdit({ fromHost: r.host, fromPath: r.path, toUrl: r.redirectedTo })
                            }
                          >
                            {t('admin.redirects.notFoundRedirect')}
                          </Button>
                          <ConfirmButton
                            variant="ghost"
                            size="sm"
                            message={t('admin.redirects.goneConfirm', { path: r.path })}
                            onConfirm={() => gone.mutateAsync(r)}
                          >
                            {t('admin.redirects.notFoundGone')}
                          </ConfirmButton>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setRowStatus.mutate({
                                id: r.id,
                                status: r.status === 'open' ? 'ignored' : 'open',
                              })
                            }
                          >
                            {r.status === 'open'
                              ? t('admin.redirects.notFoundIgnore')
                              : t('admin.redirects.notFoundReopen')}
                          </Button>
                          <HelpTip k="redirects.not_found_actions" />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {data.total > data.items.length && (
                  <p className="mt-2 text-sm text-ink-3">
                    {t('admin.redirects.notFoundMore', { shown: data.items.length, total: data.total })}
                  </p>
                )}
              </>
            )}
          </Section>
        </>
      )}
      {edit && storeId && (
        <RedirectDialog storeId={storeId} row={null} initial={edit} onClose={() => setEdit(null)} />
      )}
    </PageFrame>
  );
}

function ImportDialog({ storeId, onClose }: { storeId: string; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<RedirectImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const send = async (f: File, dryRun: boolean) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.upload<RedirectImportResult>(
        `/v1/admin/redirects/import?storeId=${storeId}&dryRun=${dryRun}`,
        fd,
      );
      if (dryRun) {
        setFile(f);
        setPreview(res);
      } else {
        toast.success(
          t('admin.redirects.imported', { created: res.summary.create, updated: res.summary.update }),
        );
        await qc.invalidateQueries({ queryKey: ['redirects', storeId] });
        onClose();
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setBusy(false);
    }
  };
  const s = preview?.summary;
  const errors = preview?.rows.filter((r) => r.action === 'error') ?? [];
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.redirects.importTitle')}
      description={t('admin.redirects.importLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={busy && !!preview}
            disabled={!file || !s || s.errors > 0 || s.create + s.update === 0}
            onClick={() => file && void send(file, false)}
          >
            {t('admin.redirects.apply')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <FilePicker
            busy={busy && !preview}
            label={t('admin.redirects.chooseFile')}
            onFile={(f) => void send(f, true)}
          />
          <DownloadButton
            path="/v1/admin/redirects/template"
            name="yonlendirme-sablonu.csv"
            label={t('admin.imports.template')}
          />
          <HelpTip k="redirects.template" />
        </div>
        {s && (
          <div className="space-y-2 text-sm" role="status">
            <p className="font-medium">{file?.name}</p>
            <p>
              {t('admin.redirects.previewSummary', {
                total: s.total,
                create: s.create,
                update: s.update,
                unchanged: s.unchanged,
                errors: s.errors,
              })}
            </p>
            {errors.length > 0 && (
              <ul className="max-h-48 space-y-1 overflow-auto rounded-sm border border-rose/30 bg-rose-tint p-3">
                {errors.slice(0, 50).map((r) => (
                  <li key={r.line}>
                    {t('admin.redirects.errorLine', { line: r.line })}: {t(`errors.${r.error}`)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
